import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import vueRouter from '@/router/index';
import { TableHeader, AddOrganizationsList, OrganizationsList, AddSendOrgList, DeleteSendOrgList, CheckOrganizationIdList, ColumnMenu, RadioSelect } from '@/shared/type';
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import SystemConst from '@/shared/system-const';
import { DataOptions } from 'vuetify';
import HttpStatus from 'http-status-codes';
import MessageConst from '@/shared/message-const';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };
    organizationSearchDialog: boolean;                              // ダイアログ表示
    sendDivisionDialog: boolean;                                    // 送付区分一括設定ダイアログ表示
    sendOrgDeleteDialog: ColumnMenu;                                // 送付組織削除ダイアログ表示
    addOrganizationTableHeaders: TableHeader[];                     // 組織選択テーブルヘッダー
    addOrganizationItem: AddOrganizationsList[];                    // ダイアログの組織一覧
    organizationsDisplayTableHeaders: TableHeader[];                // 組織一覧テーブルヘッダー
    organizationsItem: OrganizationsList[];                         // 送付先組織一覧
    selectedItemAllAddOrganization: boolean;                        // ダイアログ内組織全選択
    isAllSelectedOrganization: boolean;                             // ダイアログ内組織全選択チェックボックス操作フラグ
    selectedItemAll: boolean;                                       // 送付先全選択
    filterDefsDialog: TableFilterDefs;                              // ダイアログフィルタ定義
    filtersDialog: TableFilters;                                    // ダイアログフィルタ条件
    sendAccountDivision: number;                                    // 一括送付区分の値
    sendAccountDivisionIndividual: RadioSelect;                     // 個別送付区分の値
    sendAccountDialog: ColumnMenu;                                  // 送付区分個別設定ダイアログ
    checkTmpItems: CheckOrganizationIdList;                         // チェック済み送付先組織
    visibleColumnMenu: ColumnMenu;                                  // 行メニュー表示
    saveDialog: boolean;                                            // 一時保存ダイアログ
    totalDialog: number;                                            // ダイアログの組織一覧の総件数
    pageOptionsDialog: DataOptions;                                 // ページ情報を保存する変数（組織選択ダイアログ）
    isLoadingDialog: boolean;                                       // テーブルがロード中: true ロード中でない: false（組織選択ダイアログ）
    isLoadingSelectAll: boolean;                                    // 全選択チェックボックスがロード中: true ロード中でない: false
    total: number;                                                  // 組織一覧の総件数
    pageOptions: DataOptions;                                       // ページ情報を保存する変数
    isLoading: boolean;                                             // テーブルがロード中: true ロード中でない: false
    tmpImportantInfoId: string;                                     // 一時重要情報ID
    isOrgSelectChange: boolean;                                     // 登録組織一覧テーブル変更フラグ
    checkOrganizationIdList: CheckOrganizationIdList;               // 組織選択ダイアログ＿チェック済み組織
    tmpOrganizationIds: { [key: string]: string };                  // 組織選択ダイアログ＿一時重要情報組織ID
    selectTotal: number;                                            // 組織選択ダイアログ＿チェック済み組織総数
};

/**
 * 重要情報のお知らせ：送付ファイル組織選択
 *
 * 送付ファイル組織選択画面を表示する。
 */
export default defineComponent({
    components: { CommonTableFilter, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const tableHeightDialog = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_SELECT_ORG_DIALOG;  // テーブル高さ調整用変数（組織選択ダイアログ）
        const tableHeight = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_SEND_ORG_LIST;            // テーブル高さ調整用変数
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                                        // 1ページあたりの行数候補（10, 50, 100）
        const pageNationDialog = ref<number>(1);                                                     // ページ番号（状態管理用）（組織選択ダイアログ）
        const itemsPerPageDialog = ref<number>(linesPerPage[0]);                                     // 1ページあたりの行数（状態管理用、デフォルト値：10）（組織選択ダイアログ）
        const pageNation = ref<number>(1);                                                           // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                                           // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const sortByDialog = ref<string[]>([]);                                                      // ソートカラム（状態管理用）（組織選択ダイアログ）
        const sortDescDialog = ref<boolean[]>([]);                                                   // ソート順（状態管理用）（組織選択ダイアログ）
        const sortBy = ref<string[]>([]);                                                            // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                                         // ソート順（状態管理用）
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;                                     // テーブルのデータ取得中に表示するテキスト
        const commonRef = ref<any>(null);                                                            // メッセージダイアログ（HTTP通信失敗のエラー画面遷移用）
        const filterRef = ref<any>(null);                                                            // フィルタコンポーネント
        const bulkSelectForm = ref<any>(null);                                                       // フォーム（送付区分一括設定ダイアログ）
        const selectForm = ref<any>(null);                                                           // フォーム（送付区分個別設定ダイアログ）
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },
            organizationSearchDialog: false,
            sendDivisionDialog: false,
            sendOrgDeleteDialog: {},
            addOrganizationTableHeaders: [],
            addOrganizationItem: [],                                                                 // ダイアログの組織一覧
            organizationsDisplayTableHeaders: [],
            organizationsItem: [],
            selectedItemAllAddOrganization: false,
            isAllSelectedOrganization: false,
            selectedItemAll: false,
            filterDefsDialog: {                                                                      // ダイアログのフィルタ定義
                'organizationId': { title: '組織ID', types: 'KeyWordPartialMatch' },
                'affiliationCode': { title: '所属コード', types: 'KeyWordPartialMatch' },
                'organizationType': { title: '組織型', types: 'PullDownMulti' },
                'organizationName': { title: '組織名称', types: 'KeyWordPartialMatch' }
            },
            filtersDialog: {},                                                                       // ダイアログフィルタ条件
            sendAccountDivision: 0,
            sendAccountDivisionIndividual: {},
            sendAccountDialog: {},
            checkTmpItems: {},
            visibleColumnMenu: {},                                                                   // 行メニューの表示有無
            saveDialog: false,
            totalDialog: 0,                                                                          // ダイアログの組織一覧の総件数
            pageOptionsDialog: {                                                                     // ページ情報を保存する変数（組織選択ダイアログ）
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoadingDialog: false,                                                                  // テーブルがロード中: true ロード中でない: false（組織選択ダイアログ）
            isLoadingSelectAll: false,                                                               // 全選択チェックボックスがロード中: true、ロード中でない: false
            total: 0,                                                                                // 組織一覧の総件数
            pageOptions: {                                                                           // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                                        // テーブルがロード中: true ロード中でない: false
            tmpImportantInfoId: '0',                                                                 // 一時重要情報ID
            isOrgSelectChange: false,                                                                // 登録組織一覧テーブル変更フラグ
            checkOrganizationIdList: {},
            tmpOrganizationIds: {},
            selectTotal: 0
        });

        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];
        const router = vueRouter;
        const stepper = <number>2;                                                                   // ステッパーの2段階目
        // チェックを付けたデータ
        const addOrganizations: AddSendOrgList[] = [];
        // チェックを外したデータ
        const deleteOrganizations: DeleteSendOrgList[] = [];
        const addOrganizationTableHeaders: TableHeader[] = [
            // 組織選択一覧ヘッダ情報
            // チェックボックス
            {
                text: '',
                value: 'dataTableSelect',
                sortable: false,
                width: '5%',
                class: tableHeaderClass
            },
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '15%',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '18%',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationType',
                width: '20%',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '42%',
                class: tableHeaderClass
            }
        ];

        const organizationsDisplayTableHeaders: TableHeader[] = [
            // 設定中送付先一覧のヘッダ情報
            // チェックボックス
            {
                text: '',
                value: 'dataTableSelect',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            },
            // 組織ID
            {
                text: '組織ID',
                value: 'organizationId',
                width: '8%',
                class: tableHeaderClass
            },
            // 所属コード
            {
                text: '所属コード',
                value: 'affiliationCode',
                width: '14%',
                class: tableHeaderClass
            },
            // 組織型
            {
                text: '組織型',
                value: 'organizationType',
                width: '20%',
                class: tableHeaderClass
            },
            // 組織名称
            {
                text: '組織名称',
                value: 'organizationName',
                width: '35%',
                class: tableHeaderClass
            },
            // 送付区分
            {
                text: '送付区分',
                value: 'sendDivision',
                width: '15%',
                class: tableHeaderClass
            },
            // 行メニューアイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        const sendAccountItem = [
            SystemConst.SEND_ACCOUNT_DIVISION.SALARY,               // 給与グループ
            SystemConst.SEND_ACCOUNT_DIVISION.ORG_REPRESENTATIVE,   // 組織代表アカウント
            SystemConst.SEND_ACCOUNT_DIVISION.OFFICIAL_DOCUMENT     // 公文書グループ
        ];

        // フィルタバリデーション
        // 組織ID
        const organizationIdRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.SELECT_ORG_DIALOG,
                '組織ID'
            )
        ];
        // 所属コード
        const affiliationCodeRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.SELECT_ORG_DIALOG,
                '所属コード'
            )
        ];
        // 組織名称
        const organizationNameRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.SELECT_ORG_DIALOG,
                '組織名称'
            )
        ];
        // 送付区分
        const accountRules = [
            // 必須チェック
            (v: any) => (v !== undefined && v !== 0) || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['送付区分'])
        ];

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-send');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '重要情報のお知らせ登録');

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.importantInformation.pageSendOrg;
            itemsPerPage.value = ctx.root.$store.state.importantInformation.itemsPerPageSendOrg;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.importantInformation.sortBySendOrg;
            sortDesc.value = ctx.root.$store.state.importantInformation.sortDescSendOrg;
            // initFilterDialog()で組織選択ダイアログのフィルタ情報を初期設定
            // awaitで処理の完了を待つ
            await initFilterDialog();

            // ダイアログの初期表示ヘッダ設定
            state.addOrganizationTableHeaders = addOrganizationTableHeaders;

            // 送付先一覧の初期表示ヘッダ設定
            state.organizationsDisplayTableHeaders = organizationsDisplayTableHeaders;

            // 送付先一覧の初期表示をまだ実行していなかった場合
            if (state.tmpImportantInfoId === '0') {
                // 送付先一覧取得
                state.checkTmpItems = {};
                sendOrganizations();
            }
        }

        /**
         * 組織選択ダイアログのフィルタ情報を取得・設定
         */
        async function initFilterDialog() {
            // 重要情報のお知らせ組織選択ダイアログ組織一覧フィルタ情報取得APIでフィルタに表示する情報を取得
            // GET /api/important-information/select-organization/dialog-filter-conditions
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('importantInformation/getDialogFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 以下の項目にAPIで取得したフィルタ情報を設定
            // 組織型
            state.filterDefsDialog['organizationType'].items = response.data.organizationTypes;

            // 以下の項目にフィルタバリデーションを設定
            // 組織ID
            state.filterDefsDialog['organizationId'].validator = organizationIdRules;
            // 所属コード
            state.filterDefsDialog['affiliationCode'].validator = affiliationCodeRules;
            // 組織名称
            state.filterDefsDialog['organizationName'].validator = organizationNameRules;
        }

        /**
         * 組織選択ボタン押下処理
         * ダイアログ組織一覧情報を取得
         * @param isUpdateOptionsDialog ページ情報/ソート情報変更であるか
         */
        async function searchOrganization(isUpdateOptionsDialog: boolean) {
            // ダイアログの組織一覧を初期化する
            state.addOrganizationItem = [];

            // テーブルがロード中のフラグをtrueに設定する
            state.isLoadingDialog = true;

            // 一時重要情報IDの格納変数初期化
            let tmpImportantInfoId: number | string = 0;

            // 送付先一覧の初期表示で一時重要情報IDを取得した場合、一時重要情報IDをパラメータに設定する
            if (state.tmpImportantInfoId !== '0') {
                tmpImportantInfoId = state.tmpImportantInfoId;
            }

            // makeListParametersで現在のページ情報/ソート情報、フィルタ条件からAPIパラメータを作成する
            const params = CommonFunction.makeListParameters(state.pageOptionsDialog, state.filtersDialog);

            // ページ情報/ソート情報変更以外の場合、一時重要情報の組織IDを取得する
            if (!isUpdateOptionsDialog) {
                // 全ページ分の組織IDデータ取得APIで組織IDを取得する
                //   GET /api/important-information/check-all
                //     パラメータ：組織マスタフラグ（0）
                //                フィルタ条件（{}）
                //     レスポンス：organizationIdList
                const organizationIdList = await ctx.root.$store.dispatch('importantInformation/getCheckAllOrganizationId',
                    {
                        isOrganizationsFlag: SystemConst.IS_ORGANIZATIONS_FLAG.TEMPORARY_IMPORTANT_INFORMATION,
                        filterParams: {}
                    });

                // レスポンスのHTTPステータスコードが200以外の場合
                if (organizationIdList.status !== HttpStatus.OK) {
                    // ローダーダイアログを非表示にする
                    state.isLoading = false;
                    // エラー画面を表示し、処理を終了する
                    commonRef.value.errorDisplayTransitionControl(organizationIdList);
                }

                // 正常に処理が終了した場合（HTTPステータスコード：200）
                state.checkOrganizationIdList = organizationIdList.data.organizationIdList;
                // 取得した組織IDを組織選択ダイアログ＿一時重要情報組織IDに格納する
                for (const key in state.checkOrganizationIdList) {
                    state.tmpOrganizationIds[state.checkOrganizationIdList[key].organizationId] = state.checkOrganizationIdList[key].organizationId
                }
                // 組織選択ダイアログ＿チェック済み組織総数を設定する
                state.selectTotal = Object.keys(state.checkOrganizationIdList).length;

                // フィルタ条件がある場合、フィルタ条件に一致する一時重要情報の組織ID件数を取得する
                if (Object.keys(state.filtersDialog).length !== 0) {
                    for (const key in state.checkOrganizationIdList) {
                        if (!(state.checkOrganizationIdList[key].organizationId in state.tmpOrganizationIds)) {
                            delete state.checkOrganizationIdList[state.checkOrganizationIdList[key].organizationId];
                        }
                    }
                    // 全ページ分の組織IDデータ取得APIで組織IDを取得する
                    //   GET /api/important-information/check-all
                    //     パラメータ：組織マスタフラグ（0）
                    //                フィルタ条件（params['filterParams']）
                    //     レスポンス：tmpSelectAllOrgList
                    const tmpSelectAllOrgList = await ctx.root.$store.dispatch('importantInformation/getCheckAllOrganizationId',
                        {
                            isOrganizationsFlag: SystemConst.IS_ORGANIZATIONS_FLAG.TEMPORARY_IMPORTANT_INFORMATION,
                            filterParams: params['filterParams']
                        });

                    // レスポンスのHTTPステータスコードが200以外の場合
                    if (tmpSelectAllOrgList.status !== HttpStatus.OK) {
                        // ローダーダイアログを非表示にする
                        state.isLoading = false;
                        // エラー画面を表示し、処理を終了する
                        commonRef.value.errorDisplayTransitionControl(tmpSelectAllOrgList);
                    }

                    // 正常に処理が終了した場合（HTTPステータスコード：200）
                    // 組織選択ダイアログ＿チェック済み組織総数を設定する
                    state.selectTotal = Object.keys(tmpSelectAllOrgList.data.organizationIdList).length;
                }
            }

            // 重要情報のお知らせ組織選択ダイアログ組織一覧取得APIでデータを取得する
            // GET /api/important-information/select-organization/dialog-organization
            // パラメータ：一時重要情報ID
            //            ページ番号
            //            1ページあたりの行数
            //            ソートキー
            //            ソート順
            //            フィルタ条件
            const response = await ctx.root.$store.dispatch('importantInformation/getDialogOrganization',
                {
                    tmpImportantInfoId: tmpImportantInfoId,
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams']
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoadingDialog = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // ダイアログ組織一覧のデータをstate.addOrganizationItemへ設定する
            state.addOrganizationItem = response.data.dialogOrganizationLists;

            // 総検索件数をstate.totalDialogへ設定する
            state.totalDialog = response.data.totalCount;

            // （組織選択ダイアログ）個別チェックボックスの状態を更新する
            checkDialogUpdate();
            // （組織選択ダイアログ）全選択チェックボックスの状態を更新する
            checkAllDialogUpdate();

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoadingDialog = false;
        }

        /**
         * 組織選択ダイアログのページ情報/ソート情報変更に対するイベント
         * @param options ページ情報/ソート情報
         */
        function updateOptionsDialog(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptionsDialog = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNationDialog.value === options.page &&
                itemsPerPageDialog.value === options.itemsPerPage &&
                sortByDialog.value === options.sortBy &&
                sortDescDialog.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNationDialog.value = options.page;
            itemsPerPageDialog.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortByDialog.value = options.sortBy;
            sortDescDialog.value = options.sortDesc;

            // ダイアログ組織一覧を更新する
            searchOrganization(true);
        }

        /**
         * 組織選択ダイアログのフィルタ設定によるイベント
         * 
         * @param filters フィルタ条件
         */
        function updateFiltersDialog(filters: TableFilters) {
            // filtersをstateへ保存する
            state.filtersDialog = filters;

            // ページ番号を1へ戻す
            state.pageOptionsDialog.page = 1;
            pageNationDialog.value = 1;

            // ダイアログ組織一覧を更新する
            searchOrganization(false);
        }

        /**
         * 組織追加ダイアログ「閉じる」押下時処理
         */
        function closeOrganizationsDialog() {
            // ダイアログを閉じる
            state.organizationSearchDialog = false;
            // 組織追加・削除対象データ初期化
            addOrganizations.length = 0;
            deleteOrganizations.length = 0;
            // チェックボックスデータ初期化
            state.tmpOrganizationIds = {};
            state.checkOrganizationIdList = {};
            state.selectTotal = 0;
            // 全選択チェックボックスをfalseに変更
            state.selectedItemAllAddOrganization = false;
            state.isAllSelectedOrganization = false;
        }

        /**
         * 設定ボタン押下時処理
         */
        async function updateOrganizations() {
            // 組織選択ダイアログ＿チェック済み組織IDのデータでループを行う
            for (const key in state.checkOrganizationIdList) {
                // 組織選択ダイアログ＿一時重要情報組織IDにデータがない場合
                if (!(state.checkOrganizationIdList[key].organizationId in state.tmpOrganizationIds)) {
                    // addOrganizationsに保存する
                    addOrganizations.push({
                        organizationId: state.checkOrganizationIdList[key].organizationId,
                        affiliationCode: state.checkOrganizationIdList[key].affiliationCode
                    });
                }
            }
            // 組織選択ダイアログ＿一時重要情報組織IDのデータでループを行う
            for (const key in state.tmpOrganizationIds) {
                // 組織選択ダイアログ＿チェック済み組織IDにデータがない場合
                if (!(state.tmpOrganizationIds[key] in state.checkOrganizationIdList)) {
                    // deleteOrganizationsに保存する
                    deleteOrganizations.push({ organizationId: state.tmpOrganizationIds[key] });
                }
            }
            // addOrganizationsとdeleteOrganizationsの両方が空の場合
            if (addOrganizations.length === 0 && deleteOrganizations.length === 0) {
                // 組織選択ダイアログ＿チェック済み組織IDにデータが１件もない場合は組織が一つも選択されていないので、エラーメッセージを表示する
                if (Object.keys(state.checkOrganizationIdList).length === 0) {
                    // 共通ダイアログ simpleDisplayMessageDialog()で、メッセージを表示する
                    commonRef.value.simpleDisplayMessageDialog(CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['組織']), false);
                    return;
                } else {
                    // 組織選択ダイアログ＿チェック済み組織IDにデータがある場合は組織が一つ以上選択されているのでダイアログを閉じる
                    state.organizationSearchDialog = false;
                }
            } else {
                // ダイアログを閉じる
                state.organizationSearchDialog = false;

                // 一時重要情報IDの格納変数初期化
                let tmpImportantInfoId = '0';

                // 送付先一覧の初期表示で一時重要情報IDを取得した場合、一時重要情報IDをパラメータに設定する
                if (state.tmpImportantInfoId !== '0') {
                    tmpImportantInfoId = state.tmpImportantInfoId;
                }

                // 組織追加ダイアログ全選択チェックボックス操作フラグ設定
                const isSelectAllOrg = state.isAllSelectedOrganization;

                // makeListParametersで現在のページ情報/ソート情報、フィルタ条件からAPIパラメータを作成する
                const params = CommonFunction.makeListParameters(state.pageOptionsDialog, state.filtersDialog);

                // 一時テーブル情報登録、削除処理のAPIを呼び出す
                // URL: POST /api/important-information/select-organization/dialog-setting
                const response = await ctx.root.$store.dispatch('importantInformation/settingDialogData',
                    {
                        tmpImportantInfoId: tmpImportantInfoId,
                        addOrganizations: addOrganizations,
                        deleteOrganizations: deleteOrganizations,
                        isSelectAllOrg: isSelectAllOrg,
                        filterParams: params['filterParams']
                    }
                );

                // API通信が正常か
                if (response.status !== HttpStatus.OK) {
                    // エラー画面を表示して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                    return;
                }

                // 登録組織一覧テーブルの状態の変更
                state.isOrgSelectChange = true;
            }

            // 追加、削除情報初期化
            addOrganizations.length = 0;
            deleteOrganizations.length = 0;
            // チェックボックスデータ初期化
            state.tmpOrganizationIds = {};
            state.checkOrganizationIdList = {};
            state.selectTotal = 0;

            if (pageNation.value !== 1) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // チェック済み送付先組織の初期化
            state.checkTmpItems = {};
            // 一時重要情報テーブルデータ再取得
            sendOrganizations();
        }

        /**
         * 組織選択テーブルの全選択チェックボックス押下時処理
         * @param isSelected 全選択チェックボックス選択状態
         */
        async function selectAllDialog(isSelected: boolean) {
            // state.totalDialog が0でない場合
            if (state.totalDialog !== 0) {
                // CommonFunction.makeListParameters() を呼び出し、ページ情報・フィルタ情報のAPIパラメータを作成する
                const params = CommonFunction.makeListParameters(state.pageOptionsDialog, state.filtersDialog);

                // ローダーダイアログの表示
                state.isLoadingSelectAll = true;

                // 全ページ分の組織IDデータ取得APIで組織IDを取得する
                //   GET /api/important-information/check-all
                //     パラメータ：組織マスタフラグ（1）
                //                フィルタ条件（params['filterParams']）
                //     レスポンス：response
                const response = await ctx.root.$store.dispatch('importantInformation/getCheckAllOrganizationId',
                    {
                        isOrganizationsFlag: SystemConst.IS_ORGANIZATIONS_FLAG.M_ORGANIZATIONS,
                        filterParams: params['filterParams']
                    });

                // レスポンスのHTTPステータスコードが200以外の場合
                if (response.status !== HttpStatus.OK) {
                    // ローダーダイアログを非表示にする
                    state.isLoadingSelectAll = false;
                    // エラー画面を表示し、処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                }

                // 正常に処理が終了した場合（HTTPステータスコード：200）
                const tmpSelectAllOrgList = response.data.organizationIdList;
                // 全選択チェックボックスがtrueの場合
                if (isSelected) {
                    // 取得した組織IDでループを行う
                    for (const key in tmpSelectAllOrgList) {
                        // 組織IDが組織選択ダイアログ＿チェック済み組織IDにない場合
                        if (!(tmpSelectAllOrgList[key].organizationId in state.checkOrganizationIdList)) {
                            // 組織選択ダイアログ＿チェック済み組織IDに追加する
                            state.checkOrganizationIdList[tmpSelectAllOrgList[key].organizationId] = {
                                organizationId: tmpSelectAllOrgList[key].organizationId,
                                affiliationCode: tmpSelectAllOrgList[key].affiliationCode
                            };
                        }
                    }
                    // 組織選択ダイアログ＿チェック済み組織総数を設定する
                    state.selectTotal = Object.keys(tmpSelectAllOrgList).length;
                }

                // 全選択チェックボックスがfalseの場合
                if (!isSelected) {
                    // 取得した組織IDでループを行う
                    for (const key in tmpSelectAllOrgList) {
                        // 組織IDが組織選択ダイアログ＿チェック済み組織IDにある場合
                        if ((tmpSelectAllOrgList[key].organizationId in state.checkOrganizationIdList)) {
                            // 組織選択ダイアログ＿チェック済み組織IDから削除する
                            delete state.checkOrganizationIdList[tmpSelectAllOrgList[key].organizationId];
                        }
                    }
                    state.selectTotal = 0;
                }

                // （組織選択ダイアログ）個別チェックボックスの状態を更新する
                checkDialogUpdate();
            }
            // ローダーダイアログを非表示にする
            state.isLoadingSelectAll = false;
        }

        /**
         * 組織選択テーブルの個別チェックボックス押下時処理
         * @param selectedItem 行データ
         */
        function selectDialog(selectedItem: AddOrganizationsList) {
            // 選択された行のチェックボックスにチェックを入れ、組織選択ダイアログ＿チェック済み組織IDに選択された組織IDが存在しない場合
            if (selectedItem.dataTableSelect && !(selectedItem.organizationId in state.checkOrganizationIdList)) {
                // 組織選択ダイアログ＿チェック済み組織IDに組織IDを追加する
                state.checkOrganizationIdList[selectedItem.organizationId] = {
                    organizationId: selectedItem.organizationId,
                    affiliationCode: selectedItem.affiliationCode
                };
            }

            // 選択された行のチェックボックスからチェックを外し、組織選択ダイアログ＿チェック済み組織IDに選択された組織IDが存在する場合
            if (!selectedItem.dataTableSelect && (selectedItem.organizationId in state.checkOrganizationIdList)) {
                // 組織選択ダイアログ＿チェック済み組織IDから組織IDから削除する
                delete state.checkOrganizationIdList[selectedItem.organizationId];
            }

            if (selectedItem.dataTableSelect) {
                // チェックが入れられた場合、組織選択ダイアログ＿チェック済み組織総数に１加える
                state.selectTotal = state.selectTotal + 1;
            } else {
                // チェックが外された場合、組織選択ダイアログ＿チェック済み組織総数から１減らす
                state.selectTotal = state.selectTotal - 1;
            }

            // （組織選択ダイアログ）全選択チェックボックスの状態更新
            checkAllDialogUpdate();
        }

        /**
         * （組織選択ダイアログ）組織選択ダイアログ＿チェック済み組織IDの状態を、組織選択ダイアログのチェックボックスに反映する
         * state.checkOrganizationIdListに格納されているチェック状態をaddOrganizationsに反映する
         */
        function checkDialogUpdate() {
            // 組織選択ダイアログの現在閲覧している1ページの件数分ループを行う
            for (let i = 0; i < state.addOrganizationItem.length; i++) {
                // 組織選択ダイアログの組織IDが、組織選択ダイアログ＿チェック済み組織IDに存在するかを判定する
                if (state.addOrganizationItem[i].organizationId in state.checkOrganizationIdList) {
                    // 存在する場合
                    ctx.root.$set(state.addOrganizationItem[i], 'dataTableSelect', true);
                } else {
                    // 存在しない場合
                    ctx.root.$set(state.addOrganizationItem[i], 'dataTableSelect', false);
                }
            }
        }

        /**
         * （組織選択ダイアログ）全選択チェックボックスの更新処理
         */
        function checkAllDialogUpdate() {
            // その他ページも含め、全ての行にチェックがついており、総件数が1以上の場合、全選択のチェックボックスのチェックを入れる
            if (state.selectTotal === state.totalDialog && state.totalDialog > 0) {
                state.selectedItemAllAddOrganization = true;
            }

            // その他ページも含め、全ての行にチェックがついていない場合 または、総件数が0件の場合、全選択のチェックボックスのチェックを外す
            if (state.selectTotal !== state.totalDialog || state.totalDialog === 0) {
                state.selectedItemAllAddOrganization = false;
            }
        }

        /**
         * 送付先一覧取得
         */
        async function sendOrganizations() {
            // テーブルがロード中のフラグをtrueに設定する
            state.isLoading = true;

            // storeにページ、ソート情報を保存する
            ctx.root.$store.commit('importantInformation/setPageSendOrg', pageNation.value);
            ctx.root.$store.commit('importantInformation/setItemsPerPageSendOrg', itemsPerPage.value);
            ctx.root.$store.commit('importantInformation/setSortBySendOrg', sortBy.value);
            ctx.root.$store.commit('importantInformation/setSortDescSendOrg', sortDesc.value);

            // ソートキー（v-data-tableのheadersに設定した'value'）
            const sortKeyParam = state.pageOptions.sortBy.join(',');
            // ソート順（false => 'asc' true => 'desc'へ変換）
            const sortOrderParam = state.pageOptions.sortDesc.map(e => SystemConst.SORT_ORDER_MAPPING[Number(e)]).join(',');

            // 重要情報のお知らせ送付先組織一覧取得APIでデータを取得する
            const response = await ctx.root.$store.dispatch('importantInformation/getSendOrganization', {
                'page': String(pageNation.value),
                'itemsPerPage': String(itemsPerPage.value),
                sortKeyParam,
                sortOrderParam
            })

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 組織一覧のデータをstate.organizationsItemへ設定する
            state.organizationsItem = response.data.sendLists;
            // 組織一覧の個別チェックボックスの状態を更新する
            checkUpdate();
            // 組織一覧の全選択チェックボックスの状態を更新する
            checkAllUpdate();

            // 一時重要情報IDをstate.tmpImportantInfoIdへ設定する
            if (response.data.tmpImportantInfoId !== '0') {
                state.tmpImportantInfoId = response.data.tmpImportantInfoId;
            }

            // 総検索件数をstate.totalへ設定する
            state.total = response.data.totalCount;

            // テーブルがロード中のフラグをfalseに設定する
            state.isLoading = false;
        }

        /**
         * 送付先組織一覧のページ/ソート情報変更に対するイベント
         * @param options ページ/ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ情報をstateへ保存する
            state.pageOptions = options;

            // 状態管理用変数とoptionsの中身と比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ番号、1ページあたりの行数を設定する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            // ソートキー、ソート順を設定する
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 送付先一覧取得
            sendOrganizations();
        }

        /**
         * 送付先組織一覧の全選択チェックボックス押下時処理
         * @param isSelected 全選択チェックボックス選択状態
         */
        async function selectAll(isSelected: boolean) {
            // state.total が0でない場合
            if (state.total !== 0) {
                // 全選択チェックボックスがtrueの場合
                if (isSelected) {
                    // ローダーダイアログの表示
                    state.isLoadingSelectAll = true;

                    // 全ページ分の組織IDデータ取得APIで組織IDを取得する
                    //   GET /api/important-information/check-all
                    //     パラメータ：組織マスタフラグ（0）
                    //                フィルタ条件（{}）
                    //     レスポンス：response
                    const response = await ctx.root.$store.dispatch('importantInformation/getCheckAllOrganizationId',
                        {
                            isOrganizationsFlag: SystemConst.IS_ORGANIZATIONS_FLAG.TEMPORARY_IMPORTANT_INFORMATION,
                            filterParams: {}
                        });

                    // レスポンスのHTTPステータスコードが200以外の場合
                    if (response.status !== HttpStatus.OK) {
                        // ローダーダイアログを非表示にする
                        state.isLoadingSelectAll = false;
                        // エラー画面を表示し、処理を終了する
                        commonRef.value.errorDisplayTransitionControl(response);
                    }

                    // 正常に処理が終了した場合（HTTPステータスコード：200）
                    // チェック済み送付先組織に取得したデータを格納する
                    state.checkTmpItems = response.data.organizationIdList;
                }

                // 全選択チェックボックスがfalseの場合
                if (!isSelected) {
                    // チェック済み送付先組織を初期化する
                    state.checkTmpItems = {};
                }

                // 組織一覧の個別チェックボックスの状態を更新する
                checkUpdate();
            }
            // ローダーダイアログを非表示にする
            state.isLoadingSelectAll = false;
        }

        /**
         * 送付先組織一覧の個別チェックボックス押下時処理
         * @param selectedItem 行データ
         */
        function select(selectedItem: OrganizationsList) {
            // 選択された行のチェックボックスにチェックを入れ、チェック済み送付先組織に選択された組織IDが存在しない場合
            if (selectedItem.dataTableSelect && !(selectedItem.organizationId in state.checkTmpItems)) {
                // チェック済み送付先組織に組織IDを追加する
                state.checkTmpItems[selectedItem.organizationId] = {
                    organizationId: selectedItem.organizationId,
                    affiliationCode: selectedItem.affiliationCode
                };
            }

            // 選択された行のチェックボックスからチェックを外し、チェック済み送付先組織に選択された組織IDが存在する場合
            if (!selectedItem.dataTableSelect && (selectedItem.organizationId in state.checkTmpItems)) {
                // チェック済み送付先組織から組織IDを削除する
                delete state.checkTmpItems[selectedItem.organizationId];
            }

            // 組織一覧の全選択チェックボックスの状態を更新する
            checkAllUpdate();
        }

        /**
         * チェック済み送付先組織の状態を、一覧のチェックボックスに反映する
         * state.checkTmpItemsに格納されているチェック状態をstate.organizationsItemに反映する
         */
        function checkUpdate() {
            // 一覧の現在閲覧している1ページの件数分ループを行う
            for (let i = 0; i < state.organizationsItem.length; i++) {
                // 一覧の組織IDが、チェック済み送付先組織に存在するかを判定する
                if (state.organizationsItem[i].organizationId in state.checkTmpItems) {
                    // 存在する場合
                    ctx.root.$set(state.organizationsItem[i], 'dataTableSelect', true);
                } else {
                    // 存在しない場合
                    ctx.root.$set(state.organizationsItem[i], 'dataTableSelect', false);
                }
            }
        }

        /**
         * 全選択チェックボックスの更新処理
         */
        function checkAllUpdate() {
            // その他ページも含め、全ての行にチェックがついており、総件数が1以上の場合、全選択のチェックボックスのチェックを入れる
            if (Object.keys(state.checkTmpItems).length === state.total && state.total > 0) {
                state.selectedItemAll = true;
            }

            // その他ページも含め、全ての行にチェックがついていない場合 または、総件数が0件の場合、全選択のチェックボックスのチェックを外す
            if (Object.keys(state.checkTmpItems).length !== state.total || state.total === 0) {
                state.selectedItemAll = false;
            }
        }

        /**
         * 送付区分一括設定処理
         */
        async function setSendDivision() {
            // バリデーション実行
            if (!bulkSelectForm.value.validate()) {
                state.sendDivisionDialog = true;
                return;
            }

            // 一時重要情報IDの格納変数初期化
            let tmpImportantInfoId: number | string = 0;

            // 送付先一覧の初期表示で一時重要情報IDを取得した場合、一時重要情報IDをパラメータに設定する
            if (state.tmpImportantInfoId !== '0') {
                tmpImportantInfoId = state.tmpImportantInfoId;
            }

            const wkItems: string[] = [];
            // 全選択チェックボックスfalse場合
            if (!state.selectedItemAll) {
                // チェック済み送付先組織でループを行い、組織IDをwkItemsに格納する
                for (const key in state.checkTmpItems) {
                    wkItems.push(state.checkTmpItems[key].organizationId);
                }
            }
            // 送付区分更新API呼び出し
            const response = await ctx.root.$store.dispatch('importantInformation/setSendDivision',
                {
                    sendDivision: state.sendAccountDivision,
                    tmpImportantInfoId: tmpImportantInfoId,
                    checkTmpItemOrganizationId: wkItems,
                    isSelectedAll: state.selectedItemAll
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // チェック済み送付先組織を初期化する
            state.checkTmpItems = {};
            // 更新された送付区分を表示するため、sendOrganizations()を呼び出す
            sendOrganizations();
        }

        /**
         * 送付区分個別設定処理
         * @param items 行データ
         */
        async function setIndividualSendDivision(items: OrganizationsList) {
            // バリデーション実行
            if (!selectForm.value.validate()) {
                state.sendAccountDialog[items.organizationId] = true;
                return;
            }

            // 送付区分更新API呼び出し
            const response = await ctx.root.$store.dispatch('importantInformation/setSendDivision',
                {
                    sendDivision: state.sendAccountDivisionIndividual[items.organizationId],
                    tmpImportantInfoId: state.tmpImportantInfoId,
                    checkTmpItemOrganizationId: [items.organizationId],
                    isSelectedAll: false
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // チェック済み送付先組織を初期化する
            state.checkTmpItems = {};
            // 更新された送付区分を表示するため、sendOrganizations()を呼び出す
            sendOrganizations();
        }

        /**
         * 送付組織削除処理
         * @param items 行データ
         */
        async function deleteSendOrganization(items: OrganizationsList) {
            // 削除確認ダイアログを閉じる
            state.sendOrgDeleteDialog[items.organizationId] = false;

            // 送付先削除のAPIを呼び出す
            const response = await ctx.root.$store.dispatch('importantInformation/deleteDestinationOrg',
                {
                    tmpImportantInfoId: state.tmpImportantInfoId,
                    sequence: items.sequence
                }
            );

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 削除完了ダイアログ表示
            commonRef.value.displayMessageDialog(response);

            if (pageNation.value !== 1) {
                // ページ番号を1へ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // チェック済み送付先組織を初期化する
            state.checkTmpItems = {};
            // 送付先一覧を再取得する
            sendOrganizations();
        }

        /**
         * 「戻る」ボタンクリック処理
         */
        function back() {
            router.push('/important-information/select-creation');
        }

        /**
         * 「次へ」ボタンクリック処理
         */
        async function nextStep() {
            // 一時重要情報テーブルに情報があることを確認する
            const response = await ctx.root.$store.dispatch('importantInformation/checkExistTmp');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信が正常であった場合、画面遷移をする
            router.push('/important-information/create-sending');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            bulkSelectForm,
            selectForm,
            stepper,
            router,
            tableHeightDialog,
            tableHeight,
            linesPerPage,
            pageNationDialog,
            itemsPerPageDialog,
            pageNation,
            itemsPerPage,
            sortByDialog,
            sortDescDialog,
            sortBy,
            sortDesc,
            tableLoadingText,
            commonRef,
            filterRef,
            sendAccountItem,
            accountRules,
            searchOrganization,
            closeOrganizationsDialog,
            updateOptionsDialog,
            selectAllDialog,
            selectDialog,
            updateOrganizations,
            updateFiltersDialog,
            selectAll,
            select,
            setSendDivision,
            setIndividualSendDivision,
            deleteSendOrganization,
            sendOrganizations,
            updateOptions,
            back,
            nextStep,
            onResize
        };
    }
});
